import React from 'react';
import { useTranslation } from '@lt/localization';
import HotelRating from 'src/components/HotelRating';
import type { SearchParamsType } from 'src/routes/Hotel/store/selectors/searchParams';
import { pluralizeReviews } from 'src/utils/pluralizeUtils';
import { Link } from '@lt/components';
import type { FiltersProps } from '@lt/api/dist/methods/hotelReviews/types';
import { Sorting } from 'src/components/Sorting';
import { SORT_TYPES_REVIEWS } from 'src/components/Sorting/constants';
import type { SortingPosition } from 'src/components/Sorting/types';
import FilterButtonList from 'src/components/Reviews/components/FilterButtonList';
import type { RelinkHotelsType } from '../../HotelRelink';
import HotelRelink from '../../HotelRelink';
import Paper from '../../Paper';
import ReviewList from './ReviewList';

import styles from '../reviews.module.css';
import type { ReviewListProps } from './ReviewList/ReviewList';

type ReviewsDesktopProps = ReviewListProps & {
  isRatingExists: boolean;
  isWhitelabel: boolean;
  className?: string;
  linkedHotels: RelinkHotelsType;
  totalReviews: number;
  searchParams: SearchParamsType;
  hotelReviewsLink: string;
  sorting: SortingPosition;
  onChangeSorting: ({ order, field }: SortingPosition) => void;
  filters: FiltersProps[];
  onChangeFilters: ({
    name,
    isSelected,
  }: {
    name: string;
    isSelected: boolean;
  }) => void;
};

const ReviewsDesktop = ({
  isRatingExists,
  linkedHotels,
  searchParams,
  isWhitelabel,
  hasReviewsSection,
  totalReviews,
  className,
  isShowMoreReviews,
  hotelReviewsLink,
  isFetching,
  items,
  showMore,
  isLoadingNew,
  onChangeSorting,
  sorting,
  filters,
  onChangeFilters,
  isErrorResult,
  onClickRetry,
}: ReviewsDesktopProps) => {
  const showLinkedHotels = !isWhitelabel && linkedHotels.length > 0;
  const { t } = useTranslation();
  const selectedFiltersName = filters
    .filter((filter) => filter.isSelected)
    .map((selectedFilter) => selectedFilter.name);
  return (
    <div className={className}>
      {hasReviewsSection && (
        <div className={styles.linkTitleBlock}>
          {isWhitelabel ? (
            <div
              className={styles.linkTitle}
            >{`${totalReviews} ${pluralizeReviews(totalReviews)}`}</div>
          ) : (
            <Link
              href={hotelReviewsLink}
              className={styles.linkTitle}
            >{`${totalReviews} ${pluralizeReviews(totalReviews)}`}</Link>
          )}
        </div>
      )}
      <div className={styles.ltHotelReviewsRow}>
        <div className={styles.reviewsContainer}>
          <FilterButtonList filters={filters} onClickButton={onChangeFilters} />
          {hasReviewsSection && (
            <Sorting
              className={styles.sorting}
              isMapView={false}
              isMobile={false}
              field={sorting.field}
              awaitFiltration={false}
              changeSorting={onChangeSorting}
              sortTypes={SORT_TYPES_REVIEWS}
              order={sorting.order}
            />
          )}
          <ReviewList
            items={items}
            isShowMoreReviews={isShowMoreReviews}
            showMore={showMore}
            isLoadingNew={isLoadingNew}
            hasReviewsSection={hasReviewsSection}
            isFetching={isFetching}
            selectedFilters={selectedFiltersName}
            isErrorResult={isErrorResult}
            onClickRetry={onClickRetry}
          />
        </div>
        {(isRatingExists || showLinkedHotels) && (
          <div className={styles.rating}>
            <HotelRating />
            {showLinkedHotels && (
              <Paper padding={10}>
                <div className={styles.heading}>{t('hotel.similarHotels')}</div>
                <HotelRelink
                  hotels={linkedHotels}
                  searchParams={searchParams}
                />
              </Paper>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsDesktop;
